import { useCallback, useMemo } from 'react';
import Switch from 'components/ui/Switch';
import FloatingLabelInput from 'components/ui/FloatingLabelInput';
import { TimeDurationSmall } from 'components/ui/time-duration';
import { MAXIMUM_ALLOWED_MINUTES } from 'constants/general';
import { t } from 'services/i18n.service';
import { convertToSeconds, splitDurationSeconds } from 'utils/helpers';

interface HabitDurationCompletionProps {
  onModeChange: (value: boolean) => void;
  durationSeconds: number;
  onDurationChange: (totalSeconds: number | null) => void;
  completionValue?: string;
  onCompletionChange?: (value: string) => void;
  onLockedInteraction?: () => void;
  readOnly?: boolean;
  areOptionsLocked?: boolean;
  shouldUseTimer?: boolean;
  className?: string;
  durationError?: string;
  completionError?: string;
}

const HabitDurationCompletion: React.FC<HabitDurationCompletionProps> = ({
  onModeChange,
  durationSeconds,
  onDurationChange,
  completionValue = '',
  onCompletionChange,
  onLockedInteraction,
  readOnly = false,
  areOptionsLocked = false,
  shouldUseTimer = true,
  className = '',
  durationError,
  completionError
}) => {
  const handleToggle = (checked: boolean) => {
    onModeChange(checked);
  };

  const handleChange = useCallback(
    (time: { hours: number; minutes: number; seconds: number } | null) => {
      const value = time ? convertToSeconds(time.minutes, time.seconds) : null;
      onDurationChange(value);
    },
    [onDurationChange]
  );

  const { minutes, seconds } = useMemo(
    () => splitDurationSeconds(durationSeconds),
    [durationSeconds]
  );
  const areControlsLocked = readOnly || areOptionsLocked;

  const handleLockedInteraction = () => {
    if (areControlsLocked) {
      onLockedInteraction?.();
    }
  };

  return (
    <div className={`w-full md:w-auto flex flex-col ~gap-1/2 ${className}`}>
      <div className='flex items-center justify-between ~gap-4/6'>
        <div onPointerDownCapture={handleLockedInteraction}>
          <Switch
            checked={shouldUseTimer}
            onChange={handleToggle}
            label={t('settings_page.use_timer')}
            disabled={areControlsLocked}
            className='min-w-max'
          />
        </div>

        {shouldUseTimer ? (
          <div
            onFocusCapture={() =>
              areControlsLocked ? onLockedInteraction?.() : undefined
            }>
            <TimeDurationSmall
              initialValue={{ hours: 0, minutes, seconds }}
              onChange={handleChange}
              showHours={false}
              showSeconds={true}
              disabled={readOnly}
              maxMinutes={MAXIMUM_ALLOWED_MINUTES}
            />
          </div>
        ) : (
          <div onPointerDownCapture={handleLockedInteraction}>
            <FloatingLabelInput
              type='text'
              placeholder=''
              value={completionValue || ''}
              onChange={(e) => onCompletionChange?.(e.target.value)}
              size='small'
              label={t('settings_page.habit_finishes_when')}
              inputClassName='~pt-3/4 ~pb-2/4 ~px-2/4'
              readOnly={areControlsLocked}
              error={completionError}
              containerClassName='~w-[9.039rem]/[13.375rem]'
              onFocus={() =>
                areControlsLocked ? onLockedInteraction?.() : undefined
              }
            />
          </div>
        )}
      </div>
      {shouldUseTimer && durationError ? (
        <p className='~text-[0.625rem]/xs text-red-500 dark:text-red-400 ~pl-0/0'>
          {durationError}
        </p>
      ) : null}
    </div>
  );
};

export default HabitDurationCompletion;
